/**
 * v5.9 Addition 1 — Settings ▸ Email Ingestion panel.
 * VC Admin connects the tenant's dedicated intake mailbox once.
 * Shows the current provider, connected address, status, and a Reconnect /
 * Disconnect / Poll Now action set.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth.jsx";
import { Mail, Check, RefreshCw, PlugZap, Unplug, AlertTriangle, ExternalLink } from "lucide-react";

const PROVIDER_LOGOS = {
  gmail: "https://www.google.com/favicon.ico",
  outlook: null,
  zoho: null,
};

export default function EmailIngestion() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);
  const canManage = user?.role === "vc_admin" || user?.role === "it_admin";

  const load = useCallback(async () => {
    const { data } = await api.get("/mailbox/settings");
    setData(data);
  }, []);

  useEffect(() => {
    load();
    // Post-OAuth redirect flag
    try {
      const p = new URLSearchParams(window.location.search);
      if (p.get("connected") === "1") {
        toast.success("Mailbox connected");
        // clean the URL so a refresh doesn't re-toast
        p.delete("connected");
        const q = p.toString();
        window.history.replaceState({}, "", window.location.pathname + (q ? `?${q}` : ""));
      }
    } catch { /* ignore */ }
  }, [load]);

  const connect = async (provider) => {
    setBusy(true);
    try {
      const { data } = await api.post("/mailbox/connect/start", { provider });
      if (data.authorization_url) {
        window.location.assign(data.authorization_url);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start OAuth");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect the intake mailbox? Ingestion will stop.")) return;
    try {
      await api.post("/mailbox/disconnect");
      toast.success("Mailbox disconnected");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

 /* Commented by MG on 19Aug to test a replacement function 
 const pollNow = async () => {
    setPolling(true);
    try {
      const { data } = await api.post("/mailbox/poll");
      toast.success(
        `Poll complete · ${data.fetched} fetched · ${data.sent_to_approvals || 0} new · ${data.suppressed_duplicate || 0} duplicates`
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Poll failed");
    } finally {
      setPolling(false);
    }
  };*/
const pollNow = async () => {
    setPolling(true);
    try {
      const { data } = await api.post("/mailbox/poll");
      toast.success(
        `Poll complete · ${data.fetched} fetched · ${data.sent_to_approvals || 0} new · ${data.suppressed_duplicate || 0} duplicates`
      );
      // TEMP DEBUG: surface any per-message parse failures. Remove this
      // block once the underlying parsing bug is found and fixed on the
      // backend (the corresponding fields are also temporary there).
      if (data.skipped_with_errors) {
        const first = data.parse_errors?.[0];
        toast.error(
          `${data.skipped_with_errors} message(s) failed to parse` +
          (first ? ` · ${first.error}` : "")
        );
        console.error("mailbox poll parse_errors:", data.parse_errors);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Poll failed");
    } finally {
      setPolling(false);
    }
  };
  if (!data) return <div className="cw-card p-5 text-sm text-[var(--cw-muted)]">Loading…</div>;

  const conn = data.connection;
  const border = { borderColor: "var(--cw-border)" };

  return (
    <div className="space-y-4" data-testid="email-ingestion-panel">
      <div className="cw-card p-5 space-y-4">
        <div>
          <h3 className="cw-h3 inline-flex items-center gap-2">
            <Mail size={18} /> Deal Email Ingestion
          </h3>
          <p className="text-xs text-[var(--cw-muted)] mt-1">
            Connect one dedicated Gmail account as your fund's deal-intake mailbox.
            Fund members forward deal emails to that address; the platform
            polls it, deduplicates, and routes new emails to the{" "}
            <strong>New Deal Approvals</strong> tab for human review.
          </p>
        </div>

        {conn ? (
          <ConnectedCard conn={conn} onDisconnect={disconnect} onPoll={pollNow}
                        canManage={canManage} polling={polling} />
        ) : (
          <div className="rounded-md border p-4" style={border}>
            <div className="text-xs uppercase tracking-widest text-[var(--cw-muted)] mb-2">
              Not connected
            </div>
            <p className="text-sm text-[var(--cw-muted)] mb-3">
              Pick your mailbox provider to connect.
            </p>
            <div className="grid sm:grid-cols-3 gap-3">
              {data.providers_available.map((p) => (
                <button
                  key={p.provider_id}
                  disabled={!p.enabled || busy || !canManage}
                  onClick={() => connect(p.provider_id)}
                  data-testid={`connect-${p.provider_id}`}
                  className={`rounded-md border p-4 text-left transition ${
                    p.enabled && canManage
                      ? "hover:border-[var(--cw-primary)]"
                      : "opacity-50 cursor-not-allowed"
                  }`}
                  style={border}
                >
                  <div className="flex items-center gap-2">
                    {PROVIDER_LOGOS[p.provider_id] ? (
                      <img src={PROVIDER_LOGOS[p.provider_id]} alt="" className="h-4 w-4" />
                    ) : (
                      <Mail size={14} className="text-[var(--cw-muted)]" />
                    )}
                    <span className="font-semibold text-sm">{p.display_name}</span>
                  </div>
                  <div className="text-[11px] mt-2 text-[var(--cw-muted)]">
                    {p.enabled ? "Connect via OAuth" : "Coming soon"}
                  </div>
                </button>
              ))}
            </div>
            {!canManage && (
              <div className="text-xs text-[var(--cw-muted)] italic mt-3">
                Only VC Admins can connect a mailbox.
              </div>
            )}
          </div>
        )}
      </div>

      <div className="cw-card p-5 text-xs text-[var(--cw-muted)] space-y-2">
        <div className="font-semibold text-[var(--cw-text)] text-sm">How it works</div>
        <ol className="list-decimal list-inside space-y-1">
          <li>Superadmin/VC Admin creates a dedicated Gmail account outside the platform (e.g. <code>yourfund.deals@gmail.com</code>).</li>
          <li>Click <strong>Connect Gmail</strong> above and authorise CompeteWiser with read-only access.</li>
          <li>Share the address with your team — every deal email forwarded there enters the Approvals tab.</li>
          <li>Reviewers Accept (creating a Deal) or Reject (with reason) from the Approvals tab.</li>
        </ol>
      </div>

      <TranscriptSendersPanel canManage={canManage} />
    </div>
  );
}

/* ------------------- v5.10 A1 — Recognized Transcript Senders ------------------- */
function TranscriptSendersPanel({ canManage }) {
  const [rows, setRows] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ sender_domain: "", label: "" });
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get("/transcript-senders");
      setRows(data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.sender_domain.trim() || !form.label.trim()) {
      toast.error("Domain and label are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/transcript-senders", form);
      setForm({ sender_domain: "", label: "" });
      setShowAdd(false);
      toast.success("Sender added");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (row) => {
    try {
      await api.patch(`/transcript-senders/${row.sender_id}`,
                      { is_active: !row.is_active });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (row) => {
    if (!window.confirm(`Remove ${row.label} (${row.sender_domain})?`)) return;
    try {
      await api.delete(`/transcript-senders/${row.sender_id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const border = { borderColor: "var(--cw-border)" };
  return (
    <div className="cw-card p-5" data-testid="transcript-senders-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="cw-h3">Recognized Transcript Senders</h3>
          <p className="text-xs text-[var(--cw-muted)] mt-1">
            Add domains of meeting-transcript tools (Fathom, Otter, Fireflies, Read…)
            your fund forwards from. Emails from these domains are recognized as
            meeting transcripts and attached to the matching deal.
          </p>
        </div>
        {canManage && (
          <button onClick={() => setShowAdd((v) => !v)}
            className="cw-btn-ghost text-xs inline-flex items-center gap-1"
            data-testid="add-transcript-sender-btn">
            <span>{showAdd ? "Cancel" : "+ Add sender"}</span>
          </button>
        )}
      </div>

      {showAdd && (
        <div className="rounded-md border p-3 mb-3 space-y-2" style={border}>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[var(--cw-muted)]">Domain</label>
              <input value={form.sender_domain}
                onChange={(e) => setForm({ ...form, sender_domain: e.target.value })}
                placeholder="fathom.video"
                className="w-full h-9 px-3 rounded-md border text-sm" style={border}
                data-testid="new-sender-domain" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[var(--cw-muted)]">Label</label>
              <input value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Fathom"
                className="w-full h-9 px-3 rounded-md border text-sm" style={border}
                data-testid="new-sender-label" />
            </div>
          </div>
          <div className="text-right">
            <button onClick={add} disabled={busy}
              className="cw-btn-primary text-xs inline-flex items-center gap-1"
              data-testid="save-sender-btn">
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="text-xs text-[var(--cw-muted)] italic">
          No transcript senders configured yet. Add your fund's meeting-transcript tools above.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead style={{ background: "rgba(40,42,116,0.05)" }}>
            <tr className="text-left">
              <th className="px-3 py-2 font-semibold">Label</th>
              <th className="px-3 py-2 font-semibold">Domain</th>
              <th className="px-3 py-2 font-semibold">Status</th>
              {canManage && <th className="px-3 py-2"></th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.sender_id} className="border-t" style={border}
                  data-testid={`transcript-sender-row-${row.sender_id}`}>
                <td className="px-3 py-2 font-medium">{row.label}</td>
                <td className="px-3 py-2 text-[var(--cw-muted)]">{row.sender_domain}</td>
                <td className="px-3 py-2">
                  <span className="cw-badge"
                    style={{
                      background: row.is_active ? "rgba(44,154,82,0.12)" : "rgba(0,0,0,0.06)",
                      color: row.is_active ? "#1F6E3A" : "var(--cw-muted)",
                    }}>
                    {row.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                {canManage && (
                  <td className="px-3 py-2 text-right space-x-2">
                    <button onClick={() => toggle(row)} className="cw-btn-ghost text-xs">
                      {row.is_active ? "Deactivate" : "Activate"}
                    </button>
                    <button onClick={() => remove(row)}
                      className="text-xs text-[var(--cw-error)]">Remove</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ConnectedCard({ conn, onDisconnect, onPoll, canManage, polling }) {
  const status = conn.status || "active";
  const statusColor = {
    active: "#1F6E3A",
    auth_error: "#B02B2B",
    disabled: "#8A6510",
  }[status] || "#666";
  const statusBg = {
    active: "rgba(44,154,82,0.12)",
    auth_error: "rgba(214,69,69,0.12)",
    disabled: "rgba(243,191,92,0.20)",
  }[status] || "rgba(0,0,0,0.05)";
  return (
    <div className="rounded-md border p-4" style={{ borderColor: "var(--cw-border)" }}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--cw-muted)]">
            Connected mailbox · {conn.provider}
          </div>
          <div className="text-lg font-semibold mt-1" data-testid="mailbox-address">
            {conn.mailbox_address}
          </div>
          <div className="text-xs text-[var(--cw-muted)] mt-1">
            Sync mode: {conn.sync_mode} · Poll every {conn.poll_interval_minutes}m · Window {conn.lookback_window_minutes}m
          </div>
          {conn.last_polled_at && (
            <div className="text-xs text-[var(--cw-muted)] mt-1">
              Last polled: {conn.last_polled_at}
            </div>
          )}
        </div>
        <span
          className="cw-badge"
          style={{ background: statusBg, color: statusColor }}
          data-testid="mailbox-status"
        >
          {status === "active" ? <Check size={12} /> : <AlertTriangle size={12} />}
          {status}
        </span>
      </div>
      {status === "auth_error" && (
        <div className="mt-3 text-xs text-[var(--cw-error)]">
          Authentication failed. Reconnect the mailbox to resume ingestion.
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        {canManage && (
          <>
            <button onClick={onPoll} disabled={polling}
              className="cw-btn-primary text-xs inline-flex items-center gap-1"
              data-testid="poll-now-btn">
              <RefreshCw size={12} className={polling ? "animate-spin" : ""} />
              {polling ? "Polling…" : "Poll now"}
            </button>
            <button onClick={onDisconnect}
              className="cw-btn-ghost text-xs inline-flex items-center gap-1 text-[var(--cw-error)]"
              data-testid="disconnect-btn">
              <Unplug size={12} /> Disconnect
            </button>
          </>
        )}
        <a href={`mailto:${conn.mailbox_address}`}
          className="cw-btn-ghost text-xs inline-flex items-center gap-1"
          data-testid="copy-address-btn">
          <ExternalLink size={12} /> Send test email
        </a>
      </div>
    </div>
  );
}
